import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import * as samApi from '../api/samApi';
import { normalizeSAMUser } from '../hooks/useSAMUsers';
import { classifyAppAccount, cognitoAttributeRows, pickCognitoUser } from '../utils/cognitoUserInfo';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Notice } from './ui/Notice';
import { UserDetailDrawer } from './UserDetailDrawer';
import { useT } from '../i18n/admin';

interface AppUserInfoTabProps {
  /** `user.cognitoid` — the Cognito `sub`. */
  cognitoId?: string | null;
  email?: string | null;
  /** `user.id` of the row being viewed: an account that belongs to another row gets no actions (S-60). */
  userId?: number | null;
  /** Opens the other row's detail from the "belongs to user N" pointer. */
  onOpenUser?: (userId: number) => void;
}

/**
 * User detail → "App User Info": the Cognito account behind this app user.
 * Replaces the old Settings › App › App Users list; the drawer keeps its
 * account actions (reset password, enable/disable, groups).
 */
export const AppUserInfoTab: React.FC<AppUserInfoTabProps> = ({ cognitoId, email, userId, onOpenUser }) => {
  const { fmt, t } = useT();
  const formatDate = (value?: string) => fmt.dateTime(value) || '—';
  const [drawerOpen, setDrawerOpen] = useState(false);
  // `user.cognitoid` is the Cognito `sub`, the stable link. The e-mail is searched only for rows
  // that were never linked (no cognitoid): with a dead sub, the same e-mail is another account (S-60).
  const sub = (cognitoId || '').trim();
  const queries = [sub || (email || '').trim()].filter(Boolean);
  const lookup = queries.join(' / ');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['sam-users-search', 'app-user-info', ...queries],
    queryFn: async () => {
      for (const q of queries) {
        const response = (await samApi.searchUsers(q)) as any;
        const raw: any[] = response?.user ? [response.user] : response?.users ?? [];
        const match = pickCognitoUser(raw, sub, email);
        if (match) return match;
      }
      return null;
    },
    enabled: queries.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const account = useMemo(() => classifyAppAccount(data ?? null, userId, !!sub), [data, userId, sub]);
  const attributes = useMemo(() => (data ? cognitoAttributeRows(data) : []), [data]);
  // S-60: the account actions carry the sub of the account that was found as expected_sub.
  const samUser = useMemo(() => (data ? { ...normalizeSAMUser(data), sub: data.sub } : null), [data]);

  if (!lookup) {
    return <Notice variant="warn">This user has no e-mail or Cognito id, so there is no app account to look up.</Notice>;
  }
  if (isLoading) {
    return <p className="p-4 text-sm text-[var(--ink-500)]">Loading Cognito account…</p>;
  }
  if (isError) {
    return <Notice variant="error">Could not load the Cognito account for this user.</Notice>;
  }
  if (account?.kind === 'deleted') {
    return (
      <Notice variant="warn">
        {t('appUser.deleted', { lookup })}
      </Notice>
    );
  }
  if (account?.kind === 'other-row') {
    return (
      <Notice variant="warn">
        {t('appUser.otherRow', { id: account.userId })}{' '}
        {onOpenUser && (
          <Button variant="ghost" onClick={() => onOpenUser(account.userId)}>
            {t('appUser.openUser', { id: account.userId })}
          </Button>
        )}
      </Notice>
    );
  }
  if (!data) {
    return <Notice variant="info">No Cognito account matches this user ({lookup}).</Notice>;
  }

  const unverified = account?.kind === 'unverified';
  const groups: string[] = data.groups ?? [];
  const facts = [
    { label: 'Created', value: formatDate(data.created) },
    { label: 'Last modified', value: formatDate(data.last_modified) },
  ];

  return (
    <div className="flex flex-col gap-3">
      {unverified && <Notice variant="warn">{t('appUser.unverified')}</Notice>}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold text-[var(--ink-900)]">{data.username}</div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Badge>{data.status || '—'}</Badge>
              <Badge variant={data.enabled === false ? 'inactive' : 'active'} showDot>
                {data.enabled === false ? 'Disabled' : 'Enabled'}
              </Badge>
              {groups.map((g) => <Badge key={g} variant="invited">{g}</Badge>)}
            </div>
          </div>
          {!unverified && (
            <Button variant="ghost" icon={<Settings2 size={14} />} onClick={() => setDrawerOpen(true)}>
              Manage account
            </Button>
          )}
        </div>
        <dl className="m-0 mt-3 grid grid-cols-2 gap-x-6 gap-y-1 border-t border-[var(--line-subtle)] pt-3 text-[13px] sm:grid-cols-4">
          {facts.map((f) => (
            <div key={f.label}>
              <dt className="text-xs text-[var(--ink-500)]">{f.label}</dt>
              <dd className="m-0 text-[var(--ink-900)]">{f.value}</dd>
            </div>
          ))}
          <div>
            <dt className="text-xs text-[var(--ink-500)]">Groups</dt>
            <dd className="m-0 text-[var(--ink-900)]">{groups.length || 'None'}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--ink-500)]">Attributes</dt>
            <dd className="m-0 text-[var(--ink-900)]">{attributes.length}</dd>
          </div>
        </dl>
      </Card>

      <Card title="Cognito attributes">
        <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-2.5 text-[13px] sm:grid-cols-2 xl:grid-cols-3">
          {attributes.map((row) => (
            <div key={row.name} className="min-w-0">
              <dt className="truncate text-xs text-[var(--ink-500)] font-[family-name:var(--mono)]" title={row.name}>
                {row.name}
              </dt>
              <dd className="m-0 break-all text-[var(--ink-900)]">{row.value}</dd>
            </div>
          ))}
        </dl>
      </Card>

      {!unverified && <UserDetailDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} user={samUser} />}
    </div>
  );
};
